import {
  DevHubBox,
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubIconButton,
  DevHubLink,
  DevHubMenuItem,
  DevHubTextField,
  DevHubTooltip,
  DevHubModal,
  DevHubTypography,
  DevHubButton,
  devhubMakeStyles,
  type DevHubTheme,
} from '@devxdiscover/devhub-ui';
import { ChangeEvent, useContext, useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import ApplicationGroupSearch from '../common/ApplicationGroupSearch';
import { PartySearch } from '../common/PartySearch';
import { SystemContext } from '../../contexts/SystemContext';
import { ResponsibleParty } from './types/RoleTypes';
import ServiceDomains from './ServiceDomains';
import { InformationClassificationPolicy } from './types/PolicyTypes';
import { ClassificationSelect } from '../common/ClassificationSelect';
import {
  Application,
  ApplicationGroup,
  FunctionalArea,
  OptionItem,
} from './types/InsightsTypes';
import { ServiceTier } from './ServiceTier';
import FunctionalAreaSelect from '../common/FunctionalAreaSelect';
import { ResourceCountsChecklist } from './decom/ResourceCountsChecklist';
import { ConfirmModal } from '../common/ConfirmModal';
import useGoogleAnalytics from '../../util/useGoogleAnalytics';
import { GA4_EVENTS } from '../../util/analyticsEvents';

import {
  EditAppMutation,
  UpdateFunctionalAreaConnection,
} from './queries/InsightsQueries';
import { CorporateEntity } from '../onboarding/types/onboardingTypes';
import {
  useNotification,
  useOwnershipChangeNotifications,
} from '../notifications/hooks';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';

const useStyles = devhubMakeStyles((theme: DevHubTheme) => ({
  warningBox: {
    backgroundColor: `${theme.palette.warning.main}1A`,
    border: `1px solid ${theme.palette.warning.main}80`,
    borderRadius: 8,
  },
  warningIcon: {
    color: theme.palette.warning.main,
    marginRight: 12,
    fontSize: 24,
  },
}));

interface AppEditInput {
  [key: string]: string | number | undefined;
  applicationId?: number;
  appName?: string;
  description?: string;
  status?: string;
  serviceLevelTier?: number;
  ownerLogin?: string;
  appGroup?: number;
  serviceDomain?: number;
  url?: string;
  informationClassificationPolicy?: string;
}

interface Props {
  children: any;
  applicationId: string;
  edit: boolean;
  editComponent: string;
  editField: string;
  appInfo?: Application;
  corporateEntity?: string;
  editValue?: any;
  options?: any;
  hasEditPermission?: boolean;
}

/**
 * Component wrapper to allow editing of fields on an application
 * @param props props of the component
 * @param props.children the child component you wish to edit
 * @param props.applicationId the id of the application being editted
 * @param props.edit boolean to represent state of being edited
 * @param props.editComponent string of the type of component used for editing
 * @param props.editField the field name of the value being edited
 * @param props.appInfo the application object
 * @param props.editValue the value of the application being edited
 * @param props.options the list of options available if using a select component
 */
function EditableApp360Field(props: Props) {
  const classes = useStyles();
  const { sendEvent } = useGoogleAnalytics();
  const [editField, setEditField] = useState<boolean>(false);
  const { app, GetApplicationQuery, applicationVariables, getPolicyAccess } =
    useContext(SystemContext);
  const [selectedParty, setSelectedParty] = useState<ResponsibleParty>(
    {} as ResponsibleParty,
  );
  const [input, setInput] = useState<AppEditInput>({} as AppEditInput);
  const [infoClass, setInfoClass] = useState<InformationClassificationPolicy>(
    {} as InformationClassificationPolicy,
  );
  const [appGroup, setAppGroup] = useState<ApplicationGroup>(
    {} as ApplicationGroup,
  );
  const [functionalArea, setFunctionalArea] = useState<FunctionalArea>(
    {} as FunctionalArea,
  );
  const [currentValue, setCurrentValue] = useState<string | number>('');
  const [componentHasError, setComponentHasError] = useState<boolean>(false);
  // Retire confirmation state
  const [retireAcknowledged, setRetireAcknowledged] = useState<boolean>(false);
  const [checkListModalOpen, setCheckListModalOpen] = useState<boolean>(false);
  const [allResourcesChecked, setAllResourcesChecked] =
    useState<boolean>(false);
  const [resourcesLoading, setResourcesLoading] = useState<boolean>(true);
  const [currentUser, setCurrentUser] = useState<string>('');
  const [ownershipChanged, setOwnershipChanged] = useState<boolean>(false);

  const { getCurrentUser } = useNotification();
  const { notifyOwnershipChange } = useOwnershipChangeNotifications();

  // Get current user on component mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const user = await getCurrentUser();
      if (user) {
        setCurrentUser(user);
      }
    };
    fetchCurrentUser();
  }, [getCurrentUser]);

  const [editApp, { loading }] = useMutation(EditAppMutation, {
    refetchQueries: [
      {
        query: GetApplicationQuery,
        variables: applicationVariables,
      },
      'GetApplicationDetails',
    ],
    onCompleted: () => {
      // Emit analytics event only after successful retirement mutation
      if (props?.editField === 'status' && input?.status === 'Retired') {
        sendEvent({
          action: GA4_EVENTS.DECOM_RETIRE_SAVED,
          subject: props?.appInfo?.name || props.applicationId,
          attributes: {
            applicationId: props.applicationId,
            status: input?.status,
          },
        });
      }
      // Trigger notification effect if owner field was edited
      if (props?.editField === 'ownerLogin') {
        setOwnershipChanged(true);
      }
    },
  });

  // Send ownership change notification when mutation completes
  useEffect(() => {
    const sendOwnerNotification = async () => {
      if (ownershipChanged && currentUser) {
        setOwnershipChanged(false); // Reset flag

        await notifyOwnershipChange({
          appName: app?.name || 'Unknown',
          appId: props.applicationId,
          newOwnerLogin: selectedParty?.login,
          originalOwnerEmail: app?.owner?.email,
          currentUserEntityRef: currentUser,
        });
      }
    };

    sendOwnerNotification();
  }, [
    ownershipChanged,
    currentUser,
    notifyOwnershipChange,
    app?.name,
    app?.owner?.email,
    props.applicationId,
    selectedParty?.login,
  ]);

  const [updateFunctionalAreaConnection, { loading: functionalAreaLoading }] =
    useMutation(UpdateFunctionalAreaConnection, {
      refetchQueries: [
        {
          query: GetApplicationQuery,
          variables: applicationVariables,
        },
        'GetApplicationDetails',
      ],
    });

  const handleEdit = () => {
    // reset acknowledgement when entering edit mode
    setRetireAcknowledged(false);
    setCurrentValue(props?.editValue);
    setEditField(prevEditField => !prevEditField);
  };

  const handleClose = () => {
    setEditField(false);
    setCheckListModalOpen(false);
    setRetireAcknowledged(false);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputField = e?.target?.name;
    const newValue = e?.target?.value;

    const newInput = input;
    newInput[inputField] = newValue;
    newInput.applicationId = Number(props?.applicationId);

    setCurrentValue(newValue);
    setInput(newInput);

    // Open acknowledgement modal when selecting Retired status
    if (props?.editField === 'status' && newValue === 'Retired') {
      sendEvent({
        action: GA4_EVENTS.DECOM_WORKFLOW_OPENED,
        subject: props?.appInfo?.name || props.applicationId,
        attributes: {
          applicationId: props.applicationId,
          selectedStatus: newValue,
        },
      });
      setCheckListModalOpen(true);
      setRetireAcknowledged(false);
    }
  };

  const nameValidation = (value: string) => {
    const regex = /[!@#$%^&*(),.?":{}`|<>]/g;
    return !!(value?.length > 75 || value?.match(regex));
  };

  const descriptionValidation = (value: string) => {
    const regex = /[!@#$%^*?"{}`|<>]/g;
    return !!(
      value?.length > 1000 ||
      value?.length < 20 ||
      value?.match(regex)
    );
  };

  const urlValidation = (value: string) => {
    if (value === '') return true;
    try {
      const url = new URL(value);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const statusValidation = (value: string) => {
    if (value !== 'Retired') {
      return true;
    }
    return getPolicyAccess('RetireApplicationPolicy');
  };

  const submitValidation = () => {
    if (selectedParty?.login === 'clear') {
      return false;
    }
    if (functionalArea?.id === 'clear') {
      return false;
    }
    if (input?.appName && nameValidation(input?.appName)) {
      return false;
    }
    if (input?.url && !urlValidation(input?.url)) {
      return false;
    }
    if (input?.description && descriptionValidation(input?.description)) {
      return false;
    }
    if (input?.status && !statusValidation(input?.status)) {
      return false;
    }
    // Require acknowledgement for retiring
    if (input?.status === 'Retired' && !retireAcknowledged) {
      return false;
    }
    if (input?.serviceLevel && componentHasError) {
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (Object.keys(selectedParty).length > 0) {
      const newInput = input;
      newInput.ownerLogin = selectedParty?.login;
      newInput.applicationId = Number(props?.applicationId);
      setInput(newInput);
    }

    if (Object.keys(appGroup).length > 0) {
      const newInput = input;
      newInput.appGroup = Number(appGroup?.applicationId);
      newInput.applicationId = Number(props?.applicationId);
      setInput(newInput);
    }

    if (Object.keys(infoClass).length > 0) {
      const newInput = input;
      newInput.informationClassificationPolicy = infoClass.name;
      newInput.applicationId = Number(props?.applicationId);
      setInput(newInput);
    }

    if (functionalArea) {
      const newInput = input;
      newInput.functionalAreaId = functionalArea?.id;
      newInput.applicationId = Number(props?.applicationId);
      setInput(newInput);
    }

    if (submitValidation()) {
      switch (props?.editField) {
        case 'functionalArea':
          updateFunctionalAreaConnection({ variables: { input: input } });
          break;
        default:
          editApp({ variables: { input: input } });
          break;
      }
      handleClose();
    }
  };

  // Handlers for retire modal
  const handleConfirmRetire = () => {
    sendEvent({
      action: GA4_EVENTS.DECOM_RETIRE_ACKNOWLEDGED,
      subject: props?.appInfo?.name || props.applicationId,
      attributes: {
        applicationId: props.applicationId,
      },
    });
    setRetireAcknowledged(true);
    setCheckListModalOpen(false);
  };

  const handleCancelRetire = () => {
    // If cancelled, restore the original status value
    if (props?.editField === 'status') {
      const newInput = { ...input } as any;
      // Restore original status
      newInput.status = props?.editValue;
      setInput(newInput);
      setCurrentValue(props?.editValue || '');
    }
    setRetireAcknowledged(false);
    setCheckListModalOpen(false);
  };

  const handleRetireModalClose = () => {
    setCheckListModalOpen(false);
  };

  const editComponent = () => {
    switch (props?.editComponent) {
      case 'textArea':
        return (
          <DevHubTextField
            name={props?.editField}
            onChange={handleChange}
            error={descriptionValidation(currentValue?.toString())}
            helperText={
              descriptionValidation(currentValue?.toString())
                ? 'Description must be between 20-1000 characters, and not contain special characters.'
                : ''
            }
            variant="outlined"
            multiline
            value={currentValue}
          />
        );
      case 'textField':
        return (
          <DevHubTextField
            size="small"
            name={props?.editField}
            error={nameValidation(currentValue?.toString())}
            helperText={
              nameValidation(currentValue?.toString())
                ? 'Name must be less than 75 characters, only special characters that are allowed: . & -'
                : ''
            }
            variant="outlined"
            onChange={handleChange}
            value={currentValue}
          />
        );
      case 'urlEdit':
        return (
          <DevHubTextField
            size="small"
            name={props?.editField}
            error={!urlValidation(currentValue?.toString())}
            label="Enter URL"
            helperText={
              !urlValidation(currentValue?.toString())
                ? 'Must be a valid url.'
                : ''
            }
            variant="outlined"
            onChange={handleChange}
            value={currentValue}
          />
        );
      case 'select':
        return (
          <DevHubTextField
            name={props?.editField}
            size="small"
            select
            variant="outlined"
            label="Select"
            onChange={handleChange}
            value={currentValue}
          >
            {props?.options?.map((option: OptionItem) => {
              return (
                <DevHubMenuItem key={option.value} value={option.value}>
                  {option.label}
                </DevHubMenuItem>
              );
            })}
          </DevHubTextField>
        );
      case 'serviceLevelTier':
        return (
          <ServiceTier
            onChange={handleChange}
            setComponentHasError={setComponentHasError}
            app={props?.appInfo}
            options={props?.options}
          />
        );
      case 'appGroup':
        return (
          <ApplicationGroupSearch
            selectedAppGroup={appGroup}
            setAppGroup={setAppGroup}
            helperText="A grouping of one or more Components that share the same release timelines, dependencies and resources. An Application has many Components."
          />
        );
      case 'functionalArea':
        return (
          <FunctionalAreaSelect
            noMargin={false}
            selectedFunctionalArea={functionalArea}
            setFunctionalArea={setFunctionalArea}
          />
        );
      case 'userSelect':
        return (
          <PartySearch
            entity={app?.corporateEntity as CorporateEntity}
            helperText="Assign new owner. Compliance requirements prevent contractors from being assigned as Application Owner."
            contractors={false}
            groupEnabled={false}
            setSelectedParty={setSelectedParty}
          />
        );
      case 'informationClassification':
        return (
          <ClassificationSelect
            name="icp"
            size="small"
            setSelectedParty={setInfoClass}
            selectedParty={infoClass}
            required={false}
          />
        );
      case 'domainSelect':
        return (
          <ServiceDomains handleChange={handleChange} value={currentValue} />
        );
      case 'statusSelect': {
        const validation = statusValidation(currentValue.toString());
        const retirePolicy = app?.authorizationPolicy?.find(
          x => x.policy === 'RetireApplicationPolicy',
        );
        const errorCode = retirePolicy?.data?.errorCode;

        return (
          <DevHubTextField
            name={props?.editField}
            size="small"
            select
            error={!validation}
            helperText={(() => {
              if (!validation) {
                switch (errorCode) {
                  case 'HAS_ACTIVE_CONNECTIONS':
                    return 'Cannot retire application with active device/cloud account connections.';
                  case 'MISSING_AD_GROUP_MEMBERSHIP':
                    return (
                      <div>
                        To retire this application, please contact{' '}
                        <DevHubLink
                          target="_blank"
                          href="mailto:Cyber_Security-Third_Party_Security_Assessments@comcast.com"
                        >
                          Cyber_Security-Third_Party_Security_Assessments@comcast.com
                        </DevHubLink>{' '}
                      </div>
                    );
                  default:
                    return '';
                }
              }
              return '';
            })()}
            variant="outlined"
            label="Select"
            onChange={handleChange}
            value={currentValue}
          >
            {props?.options?.map((option: OptionItem) => {
              return (
                <DevHubMenuItem key={option.value} value={option.value}>
                  {option.label}
                </DevHubMenuItem>
              );
            })}
          </DevHubTextField>
        );
      }
      default:
        return <></>;
    }
  };

  return (
    <DevHubBox display="flex">
      {props.edit && editField ? editComponent() : <>{props.children}</>}
      {props.edit && !editField && (
        <>
          {loading || functionalAreaLoading ? (
            <DevHubCircularProgress thickness={6} size={22} />
          ) : (
            <DevHubTooltip title="Edit">
              <DevHubBox>
                <DevHubIconButton
                  size="medium"
                  disabled={!props.hasEditPermission}
                  onClick={handleEdit}
                >
                  <DevHubDynamicIcon iconName="Edit" />
                </DevHubIconButton>
              </DevHubBox>
            </DevHubTooltip>
          )}
        </>
      )}
      {props.edit && editField && (
        <DevHubBox display="inline-flex" ml={4} pt={2}>
          <DevHubTooltip title="Cancel">
            <DevHubBox>
              <DevHubIconButton onClick={handleClose}>
                <DevHubDynamicIcon iconName="CancelOutlined" />
              </DevHubIconButton>
            </DevHubBox>
          </DevHubTooltip>
          <DevHubTooltip title="Save">
            <DevHubBox>
              <DevHubIconButton
                disabled={!submitValidation()}
                onClick={() => handleSubmit()}
              >
                <DevHubDynamicIcon iconName="Save" />
              </DevHubIconButton>
            </DevHubBox>
          </DevHubTooltip>
        </DevHubBox>
      )}
      {/* Checklist modal for app retirement with resource counts */}
      {ConfigHelper.instance.isFeatureEnabled(Feature.APP_DECOM_WORKFLOW) ? (
        <DevHubModal open={checkListModalOpen} onClose={handleRetireModalClose}>
          <>
            <DevHubBox p={3}>
              <DevHubTypography variant="h4" fontColor="primary">
                Application Retirement Checklist
              </DevHubTypography>
              <DevHubTypography variant="body1" style={{ marginTop: 8 }}>
                Before retiring this application, please review the associated
                resources below. Ensure all resources have been properly
                decommissioned or migrated.
              </DevHubTypography>
            </DevHubBox>
            <DevHubBox px={3} pb={2}>
              <ResourceCountsChecklist
                applicationId={Number(props?.applicationId)}
                applicationName={props?.appInfo?.name}
                onAllResourcesCleared={setAllResourcesChecked}
                onLoadingChange={setResourcesLoading}
              />
            </DevHubBox>
            {allResourcesChecked && (
              <DevHubBox px={3} pb={2}>
                <DevHubBox p={2} className={classes.warningBox}>
                  <DevHubBox display="flex" alignItems="flex-start">
                    <DevHubDynamicIcon
                      iconName="WarningOutlined"
                      className={classes.warningIcon}
                    />
                    <DevHubTypography variant="body2">
                      <strong>This action cannot be undone.</strong> Once
                      retired, you will no longer be able to edit any
                      information related to this application. By clicking
                      &quot;I Understand & Agree&quot;, you confirm that you
                      have reviewed all resources and accept the retirement
                      terms.
                    </DevHubTypography>
                  </DevHubBox>
                </DevHubBox>
              </DevHubBox>
            )}
            <DevHubBox p={3} display="flex" justifyContent="space-between">
              <DevHubButton
                variant="contained"
                buttonColor="orangeRed"
                onClick={handleCancelRetire}
              >
                Cancel
              </DevHubButton>
              {resourcesLoading || !allResourcesChecked ? (
                <DevHubTooltip
                  title={
                    resourcesLoading
                      ? 'Loading resource counts...'
                      : 'All mandatory resource counts must be zero before retiring'
                  }
                >
                  <span>
                    <DevHubButton
                      variant="contained"
                      buttonColor="medGray"
                      disabled
                    >
                      I Understand & Agree
                    </DevHubButton>
                  </span>
                </DevHubTooltip>
              ) : (
                <DevHubTooltip title="This action cannot be undone">
                  <span>
                    <DevHubButton
                      variant="contained"
                      buttonColor="blueGreen"
                      onClick={handleConfirmRetire}
                    >
                      I Understand & Agree
                    </DevHubButton>
                  </span>
                </DevHubTooltip>
              )}
            </DevHubBox>
          </>
        </DevHubModal>
      ) : (
        <ConfirmModal
          open={checkListModalOpen}
          message="You have selected to retire this application. This action cannot
              be undone. Once retired, you will no longer be able to edit any
              information related to this application. Please confirm you
              understand, and agree."
          onConfirm={handleConfirmRetire}
          onCancel={handleCancelRetire}
        />
      )}
    </DevHubBox>
  );
}

export default EditableApp360Field;
